import { Droppable } from "@hello-pangea/dnd";
import {
  ArrowLeft,
  ArrowRight,
  MoreHorizontal,
  Plus,
  Trash2,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useMemo, useRef } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type {
  PipelineCardSummary,
  PipelineStage,
  Sale,
  Tag,
} from "../../types";
import { InlineText } from "../InlineText";
import { LoadMore } from "../LoadMore";
import { applyPendingMoves, type PendingMove } from "../pendingMoves";
import { sortByPosition } from "../positions";
import { useFormatAmount } from "../useFormatAmount";
import { PipelineCardItem } from "./PipelineCardItem";
import { useInfiniteStageCards } from "./usePipelineData";

export interface StageColumnProps {
  stage: PipelineStage;
  /** Server filter of the board, without the stage. */
  filter: Record<string, unknown>;
  pendingMoves: Record<string, PendingMove<PipelineCardSummary>>;
  /** Exposes the displayed cards, needed to compute drop positions. */
  onItemsChange: (stageId: string, cards: PipelineCardSummary[]) => void;
  tagsById: Map<number, Tag>;
  salesById: Map<Sale["id"], Sale>;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onRename: (name: string) => Promise<void>;
  onMove: (direction: -1 | 1) => void;
  onAddStageAfter: () => void;
  onDelete: () => void;
  onAddCard: () => void;
  onOpenCard: (card: PipelineCardSummary) => void;
}

/** One stage of the board, loading 25 cards at a time while scrolling. */
export const StageColumn = ({
  stage,
  filter,
  pendingMoves,
  onItemsChange,
  tagsById,
  salesById,
  canMoveLeft,
  canMoveRight,
  onRename,
  onMove,
  onAddStageAfter,
  onDelete,
  onAddCard,
  onOpenCard,
}: StageColumnProps) => {
  const translate = useTranslate();
  const formatAmount = useFormatAmount();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stageId = String(stage.id);
  const columnFilter = useMemo(
    () => ({ ...filter, stage_id: stage.id }),
    [filter, stage.id],
  );
  const {
    cards,
    total,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteStageCards(columnFilter);

  const { items, countDelta } = useMemo(() => {
    const placed = applyPendingMoves(
      cards,
      pendingMoves,
      stageId,
      (card, position) => ({ ...card, stage_id: stage.id, position }),
    );
    return {
      items: sortByPosition(placed.items),
      countDelta: placed.countDelta,
    };
  }, [cards, pendingMoves, stageId, stage.id]);

  useEffect(() => {
    onItemsChange(stageId, items);
  }, [onItemsChange, stageId, items]);

  // The sum is exact only once every card of the stage is loaded
  const amount = hasNextPage
    ? 0
    : items.reduce((sum, card) => sum + card.deals_amount, 0);

  return (
    <section
      className="flex w-72 shrink-0 flex-col rounded-lg bg-muted/40"
      aria-label={stage.name}
      data-testid="stage-column"
    >
      <header className="flex items-start gap-1 p-2">
        <div className="min-w-0 flex-1">
          <InlineText
            value={stage.name}
            required
            label={translate("crm.operations.stages.name")}
            onSave={onRename}
            className="h-7 text-sm font-semibold"
            displayClassName="truncate text-sm font-semibold"
          />
          <p className="px-1 text-xs text-muted-foreground">
            {total + countDelta}
            {amount > 0 && ` · ${formatAmount(amount)}`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onAddCard}
          aria-label={translate("crm.operations.cards.add_to_stage", {
            stage: stage.name,
          })}
          title={translate("crm.operations.cards.add_to_stage", {
            stage: stage.name,
          })}
        >
          <Plus />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={translate("crm.operations.stages.actions")}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={!canMoveLeft}
              onSelect={() => onMove(-1)}
            >
              <ArrowLeft />
              {translate("crm.operations.stages.move_left")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!canMoveRight}
              onSelect={() => onMove(1)}
            >
              <ArrowRight />
              {translate("crm.operations.stages.move_right")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onAddStageAfter}>
              <Plus />
              {translate("crm.operations.stages.add_after")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={onDelete}
            >
              <Trash2 />
              {translate("crm.operations.stages.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <Droppable droppableId={stageId}>
        {(provided, snapshot) => (
          <div
            ref={(element) => {
              provided.innerRef(element);
              scrollRef.current = element;
            }}
            {...provided.droppableProps}
            className={cn(
              "flex max-h-[calc(100vh-16rem)] min-h-24 flex-1 flex-col gap-2 overflow-y-auto rounded-b-lg p-2 transition-colors",
              snapshot.isDraggingOver && "bg-muted",
            )}
          >
            {items.map((card, index) => (
              <PipelineCardItem
                key={card.id}
                card={card}
                index={index}
                tagsById={tagsById}
                salesById={salesById}
                onOpen={onOpenCard}
              />
            ))}
            {provided.placeholder}
            <LoadMore
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              rootRef={scrollRef}
            />
            {!isPending && items.length === 0 && !snapshot.isDraggingOver && (
              <p className="py-6 text-center text-xs text-muted-foreground">
                {translate("crm.operations.stages.drop_here")}
              </p>
            )}
          </div>
        )}
      </Droppable>
    </section>
  );
};
